import { MonitoringPromptKpi } from '@edgebox/sync-core-data-definitions';
import type { OpenApi } from '@edgebox/sync-core-rest-client';

/**
 * WHICH MEASURE OF TONE THIS APP READS, AND WHAT IT CALLS IT.
 *
 * Sync Core answers "how do the answers speak about this subject" twice. `sentiment` is a single
 * mean it has always served. `verdict` is the newer reading: a judged distribution over a five-point
 * rubric, served as five shares plus a `score` that collapses them onto the same 0–1 scale the mean
 * uses. The second is the better-measured answer to the same question, so this app reads it — but
 * the first has not been retired, and nothing here assumes it ever will be.
 *
 * So the choice lives in ONE place. Flip {@link TONE_METRIC} and every chart, table, sort column,
 * tooltip and export in Governance follows, because none of them names a wire field itself: they
 * read through {@link toneValue}, which knows that the two metrics are shaped differently and that
 * only one of them is a bare number. Flip {@link TONE_METRIC_LABEL} and every rendered string
 * follows for the same reason.
 *
 * ## What the switch cannot move, and why
 *
 * {@link TONE_KPI} does not follow it. The purpose mask is a published server vocabulary —
 * `MonitoringPromptKpi` is exactly `visibility`, `share-of-voice`, `position`, `sentiment`,
 * `recommendation` and `attributes` — and it declares no member for the verdict. Sync Core masks
 * the verdict distributions UNDER the sentiment KPI, on the grounds that both answer the same
 * question, so a purpose that may not report tone reports neither. There is therefore no value to
 * switch that enum to: sending `verdict` would name a KPI the server does not know. It is exported
 * from here anyway so that no screen spells the enum member itself, and so this paragraph is what a
 * reader finds when they wonder why one of the three constants is fixed.
 */

/** The two measures of tone Sync Core serves. Only one of them is read at a time. */
export type ToneMetricName = 'sentiment' | 'verdict';

/**
 * THE SWITCH. Which measure every Governance surface reads.
 *
 * `verdict` today: the same question, answered over a judged rubric rather than a single mean.
 */
export const TONE_METRIC: ToneMetricName = 'verdict';

/**
 * THE LABEL. What every Governance surface calls that measure, wherever a person can read it.
 *
 * Titles, axis names, column headers, tooltips, legends and screen-reader text all take their
 * wording from here, so the app never says "Sentiment" while plotting the verdict.
 */
export const TONE_METRIC_LABEL = 'Verdict';

/**
 * THE LABEL, lower case, for the places a sentence puts it mid-clause — "so verdict could not be
 * measured", "verdict: 42%".
 *
 * Its own constant rather than `TONE_METRIC_LABEL.toLowerCase()`, because the two are one wording
 * decision and a name that should stay capitalised mid-sentence has nowhere else to say so.
 */
export const TONE_METRIC_LABEL_LOWER = 'verdict';

/**
 * The KPI the purpose mask hides this measure behind — FIXED at the sentiment member, which is the
 * only one the published vocabulary has for tone. See the module docblock.
 */
export const TONE_KPI = MonitoringPromptKpi.Sentiment;

/**
 * What ONE MENTION's own tone reading is called — and it does not follow the switch.
 *
 * The verdict is judged per SUBJECT within an answer: a brand's mentions are read together and
 * graded as one. Nothing grades a single mention, so at that grain there is no verdict to show and
 * the figure on the row is the text's own sentiment. Labelling it with the switch would put the
 * verdict's name on a number the verdict never produced.
 *
 * Named here rather than written into the table, so the one surface that legitimately says
 * "Sentiment" says it for a stated reason instead of looking like a spelling the switch missed.
 */
export const MENTION_TONE_LABEL = 'Sentiment';

/**
 * What ONE PASSAGE's tone reading is called — and, like the per-mention label, it does not follow
 * the switch.
 *
 * A passage is read by the verdict judge alone: each (passage, brand) pair carries a grade, and
 * nothing else measures tone at that grain. The sentiment mean is an answer-level figure. So the
 * passage card, the colouring that shades passages by grade and the attribute verdicts combined
 * from those grades say "Verdict" whichever measure the rest of the screens read.
 */
export const PASSAGE_TONE_LABEL = 'Verdict';

/**
 * Which subjects a reading covers: the answer's own brands, its competitors, or the two pooled.
 *
 * The same three families every other measurement on these payloads splits by, under the field
 * prefixes the wire uses for them.
 */
export type ToneFamily = 'own' | 'competitor' | 'average';

/**
 * One served verdict distribution, of which only the collapsed scalar is read here.
 *
 * The generated model, intersected with the scalar rather than retyped around it: the five shares
 * keep exactly the shape the client declares, and `score` is named in this one place. A surface
 * wanting the distribution reads the shares off the generated type directly and draws them — this
 * switch exists to hand back one comparable number, and that is all it reads.
 */
type IVerdictDistribution = Partial<OpenApi.GovernancePromptMonitoringStatsVerdictResponse> & {
  /** The five shares as one number on the 0–1 scale the sentiment mean is served on. */
  score?: number | null;
};

/**
 * The five grades, harshest first, and the weight each one carries.
 *
 * The scale is an ordered vocabulary: -2 argues against the subject, 0 is descriptive, +2 recommends
 * it without qualification. Collapsing it to one number weights each grade by its own position.
 */
const VERDICT_GRADE_WEIGHTS: readonly [keyof OpenApi.GovernancePromptMonitoringStatsVerdictResponse, number][] = [
  ['arguesAgainst', -2],
  ['caveat', -1],
  ['neutral', 0],
  ['favourable', 1],
  ['recommends', 2],
];

const VERDICT_GRADE_MIN = -2;
const VERDICT_GRADE_MAX = 2;

/**
 * One verdict payload as a single number on 0–1, or undefined where it measures nothing.
 *
 * Sync Core serves this figure as `score` on the stats distributions, and that is what this reads
 * whenever it is there — one authority for the number, and no chance of this screen and a report
 * built elsewhere disagreeing about it.
 *
 * Where a payload carries no `score`, the five grades are collapsed here by the same rule: each
 * weighted by the grade it holds, divided by the total so counts and shares read alike, and
 * rescaled from the scale's ends onto 0–1. That covers the per-answer and per-mention readings,
 * which carry the raw grade counts rather than a served distribution, and the tally of the passages
 * that discuss one attribute for one brand — a reading of the verdict alone, which is why it is
 * exported beside the switch rather than read through it.
 *
 * What it cannot do is separate a subject argued hard both ways from one described flatly — both
 * land on 0.5, which is the cost of one number and the reason the five grades stay on the payload
 * for any surface with room to draw them.
 */
export function verdictScoreOf(distribution: IVerdictDistribution | null | undefined): number | undefined {
  if (!distribution) {
    return undefined;
  }
  if (typeof distribution.score === 'number' && Number.isFinite(distribution.score)) {
    return distribution.score;
  }

  let total = 0;
  let weighted = 0;
  for (const [key, weight] of VERDICT_GRADE_WEIGHTS) {
    const value = distribution[key];
    // A payload missing any one grade is not a partial reading to salvage: the five are one
    // measurement, and reading the rest over a short denominator would publish a confident number.
    if (typeof value !== 'number' || !Number.isFinite(value)) {
      return undefined;
    }
    total += value;
    weighted += value * weight;
  }
  if (!(total > 0)) {
    return undefined;
  }

  return (weighted / total - VERDICT_GRADE_MIN) / (VERDICT_GRADE_MAX - VERDICT_GRADE_MIN);
}

/**
 * A wire row carrying either measure, in any of the three families.
 *
 * Every field optional and nullable, because that is how both the generated client and the server
 * behave: an unmeasured figure is ABSENT rather than zero, and a purpose mask blanks the fields it
 * suppresses. A row that carries neither measure reads as unmeasured, which is what it is.
 */
export interface IToneBearingRow {
  sentiment?: number | null;
  competitorSentiment?: number | null;
  averageSentiment?: number | null;
  verdict?: IVerdictDistribution | null;
  competitorVerdict?: IVerdictDistribution | null;
  averageVerdict?: IVerdictDistribution | null;
}

/** The sentiment field each family is served under. */
const SENTIMENT_FIELD: Readonly<Record<ToneFamily, keyof IToneBearingRow>> = {
  own: 'sentiment',
  competitor: 'competitorSentiment',
  average: 'averageSentiment',
};

/** The verdict field each family is served under. */
const VERDICT_FIELD: Readonly<Record<ToneFamily, keyof IToneBearingRow>> = {
  own: 'verdict',
  competitor: 'competitorVerdict',
  average: 'averageVerdict',
};

/**
 * The tone reading of one row, on the 0–1 scale both measures share, or undefined where this row
 * carries none.
 *
 * The one place that knows the two metrics are shaped differently. `sentiment` is a bare number;
 * `verdict` is an object whose `score` collapses its five shares onto the same scale. Every caller
 * gets a number either way, which is what lets the switch above move without touching them.
 *
 * Undefined rather than 0 for an absent reading, because these payloads omit what they did not
 * measure and a zero would plot as "measured, and as negative as it gets". A caller that needs a
 * number for a chart is the one that decides what an absent reading draws as.
 */
export function toneValue(row: IToneBearingRow | null | undefined, family: ToneFamily = 'own'): number | undefined {
  if (!row) {
    return undefined;
  }

  if (TONE_METRIC === 'verdict') {
    return verdictScoreOf(row[VERDICT_FIELD[family]] as IVerdictDistribution | null | undefined);
  }

  const value = row[SENTIMENT_FIELD[family]];
  return typeof value === 'number' ? value : undefined;
}

/**
 * Whether a row carries a tone reading at all — the test a screen makes before drawing a column or
 * a series for it. Exactly `toneValue` being a number, stated once so no caller invents its own.
 */
export function hasToneValue(row: IToneBearingRow | null | undefined, family: ToneFamily = 'own'): boolean {
  return toneValue(row, family) !== undefined;
}
